import { lerp } from "../utils/math";

/**
 * Visual parameters derived from the server's timeOfDay
 * (0 = dawn, 0.25 = noon, 0.5 = dusk, 0.75 = midnight).
 */
export type DayNightState = {
  skyColor: number;
  fogColor: number;
  sunIntensity: number;
  ambientIntensity: number;
  /** Sun elevation angle in radians (peaks at noon). */
  sunAngle: number;
  isNight: boolean;
};

type Keyframe = {
  t: number;
  sky: number;
  sun: number;
  ambient: number;
};

// Wraps: the last frame interpolates back into the first (t + 1).
const KEYFRAMES: Keyframe[] = [
  { t: 0.0, sky: 0xff9a5c, sun: 0.5, ambient: 0.45 }, // dawn
  { t: 0.08, sky: 0x87ceeb, sun: 0.9, ambient: 0.6 }, // morning
  { t: 0.25, sky: 0x87ceeb, sun: 1.0, ambient: 0.65 }, // noon
  { t: 0.42, sky: 0x87ceeb, sun: 0.9, ambient: 0.6 }, // afternoon
  { t: 0.5, sky: 0xff7043, sun: 0.4, ambient: 0.4 }, // dusk
  { t: 0.58, sky: 0x131a33, sun: 0.08, ambient: 0.22 }, // nightfall
  { t: 0.75, sky: 0x0b1026, sun: 0.05, ambient: 0.18 }, // midnight
  { t: 0.92, sky: 0x131a33, sun: 0.08, ambient: 0.22 }, // late night
];

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(lerp(ar, br, t));
  const g = Math.round(lerp(ag, bg, t));
  const bl = Math.round(lerp(ab, bb, t));
  return (r << 16) | (g << 8) | bl;
}

export function getDayNight(timeOfDay: number): DayNightState {
  const t = ((timeOfDay % 1) + 1) % 1;

  // Find surrounding keyframes (wrapping around 1.0)
  let prev = KEYFRAMES[KEYFRAMES.length - 1];
  let next = KEYFRAMES[0];
  let prevT = prev.t - 1;
  let nextT = next.t;
  for (let i = 0; i < KEYFRAMES.length; i++) {
    const current = KEYFRAMES[i];
    const following = KEYFRAMES[(i + 1) % KEYFRAMES.length];
    const followingT = i + 1 < KEYFRAMES.length ? following.t : following.t + 1;
    if (t >= current.t && t < followingT) {
      prev = current;
      next = following;
      prevT = current.t;
      nextT = followingT;
      break;
    }
  }

  const span = nextT - prevT;
  const frac = span > 0 ? (t - prevT) / span : 0;

  const sky = lerpColor(prev.sky, next.sky, frac);
  const sunIntensity = lerp(prev.sun, next.sun, frac);
  const ambientIntensity = lerp(prev.ambient, next.ambient, frac);

  return {
    skyColor: sky,
    fogColor: sky,
    sunIntensity,
    ambientIntensity,
    // Sun rises at dawn (t=0), peaks at noon (t=0.25), sets at dusk (t=0.5)
    sunAngle: t * Math.PI * 2,
    isNight: sunIntensity < 0.25,
  };
}
